import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import {
    ANNIE_ELISE_ANALYTICS_IMAGE_URL,
    MANCHUREK_CASE_IMAGE,
    LIONHEART_CASE_IMAGE,
    MANCHUREK_CHANNEL_URL,
    LIONHEART_CHANNEL_URL,
} from "../constants/site";
import "./Work.css";

const SECONDARY_CASES = [
    {
        key: "manchurek-triplets",
        meta: "Manchurek Triplets / YouTube Content Creation",
        image: MANCHUREK_CASE_IMAGE,
        stat: "5.6M to 9.16M",
        statLabel: "subscribers in 3 months",
        copy: "We supercharged the Manchurek Triplets' online presence with eye-catching visuals and a sharp content strategy focused on inventive pranks. By pinpointing the perfect moments to highlight their best content, we dramatically boosted viewer retention, and tailored their posting schedule to maximize engagement.",
        url: MANCHUREK_CHANNEL_URL,
    },
    {
        key: "lionheart-filmworks",
        meta: "Lionheart Filmworks / Film Production",
        image: LIONHEART_CASE_IMAGE,
        stat: "90K to 543K",
        statLabel: "subscribers in 9 months",
        copy: "Elevating content for niche channels like Lionheart Filmworks presents unique challenges, but we have successfully driven views and retention. We built their TikTok presence from scratch with frequent uploads, optimized tags and descriptions, leading to a steady and rapid increase in followers.",
        url: LIONHEART_CHANNEL_URL,
    },
];

const MediaBlock = ({ src, alt, className, testid }) =>
    src ? (
        <img className={className} src={src} alt={alt} data-testid={testid} />
    ) : (
        <div
            className={`${className} work-media-placeholder`}
            role="img"
            aria-label={alt}
            data-testid={testid}
        />
    );

export const Work = () => {
    return (
        <section className="section work" id="work" data-testid="work-section">
            <div className="section-inner">
                <p className="eyebrow" data-testid="work-eyebrow">
                    Case Studies
                </p>
                <h2 className="section-heading" data-testid="work-heading">
                    Channels we have <span className="serif">grown</span>.
                </h2>
                <p className="section-intro" data-testid="work-intro">
                    Real numbers, taken from the clients' own analytics.
                </p>

                <Reveal
                    as="article"
                    className="work-featured"
                    data-testid="work-featured-card"
                >
                    <MediaBlock
                        src={ANNIE_ELISE_ANALYTICS_IMAGE_URL}
                        alt="[ANNIE_ELISE_ANALYTICS_IMAGE_URL] subscriber growth graph"
                        className="work-featured-media"
                        testid="work-featured-media"
                    />
                    <div className="work-featured-text">
                        <p className="work-card-meta">
                            Annie Elise / 10 to LIFE / True crime
                        </p>
                        <p
                            className="work-featured-stat"
                            data-testid="work-featured-stat"
                        >
                            400K to 1.23M
                        </p>
                        <p className="work-stat-label">
                            subscribers in 6 months
                        </p>
                        <p className="work-card-copy">
                            Annie wanted faster growth and more uploads per
                            week. We rebuilt her intros, tested new edit styles
                            for six months, and launched Serialously, a
                            podcast-style series. Her videos have now passed 20
                            million views on YouTube and 50 million across
                            platforms.
                        </p>
                        <a
                            className="work-card-link"
                            href="https://www.youtube.com/@annieelise"
                            target="_blank"
                            rel="noopener"
                            data-testid="work-featured-link"
                        >
                            Watch the channel
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </a>
                    </div>
                </Reveal>

                <div className="work-grid">
                    {SECONDARY_CASES.map((item, i) => (
                        <Reveal
                            as="article"
                            className="work-card"
                            key={item.key}
                            delay={i * 0.08}
                            data-testid={`work-card-${item.key}`}
                        >
                            <MediaBlock
                                src={item.image}
                                alt={`${item.meta} case study media`}
                                className="work-card-media"
                                testid={`work-card-media-${item.key}`}
                            />
                            <p className="work-card-meta">{item.meta}</p>
                            <p className="work-card-stat">{item.stat}</p>
                            <p className="work-stat-label">{item.statLabel}</p>
                            <p className="work-card-copy">{item.copy}</p>
                            {item.url ? (
                                <a
                                    className="work-card-link"
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener"
                                    data-testid={`work-card-link-${item.key}`}
                                >
                                    Watch the channel
                                    <ArrowUpRight size={16} aria-hidden="true" />
                                </a>
                            ) : (
                                <span
                                    className="work-card-link-placeholder"
                                    data-testid={`work-card-link-${item.key}`}
                                >
                                    [CHANNEL_URL]
                                </span>
                            )}
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};
